import { useTranslation } from "react-i18next";
import { Bell, Check, FileText, PenLine, Plus } from "lucide-react";
import { Blob } from "../ui/decorations";

/**
 * Composição gráfica do hero: uma "janela" do app desenhada em JSX/CSS
 * com conteúdo real (nota + tarefas), uma tarefa que se completa sozinha
 * em loop e cards flutuantes ao redor. Puramente decorativa (aria-hidden).
 */
export function HeroMockup() {
  const { t } = useTranslation("landing");
  return (
    <div className="relative" aria-hidden="true">
      {/* Blob laranja desfocado atrás do mockup */}
      <Blob className="inset-8 -z-10 bg-primary-300/50 blur-3xl" />

      {/* Janela do app — levemente torta, com a sombra dura assinatura da landing */}
      <div className="rounded-2xl border-2 border-ink/10 bg-white overflow-hidden -rotate-1 shadow-[10px_10px_0_0_rgba(45,20,5,0.12)]">
        {/* Barra de título */}
        <div className="flex items-center gap-1.5 px-4 py-3 border-b border-ink/5 bg-cream/60">
          <span className="w-2.5 h-2.5 rounded-full bg-red-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
          <span className="ml-3 hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-md bg-white border border-ink/10 text-[10px] font-semibold text-ink/40">
            taskletto.app
          </span>
        </div>

        <div className="flex">
          {/* Sidebar com notas "de verdade" */}
          <div className="hidden sm:block w-40 shrink-0 border-r border-ink/5 p-3 bg-zinc-50">
            <p className="px-2 mb-2 text-[9px] font-bold uppercase tracking-wider text-ink/35">
              {t("mockup.myNotes")}
            </p>
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-primary-100 border border-primary-200">
                <FileText size={11} className="text-primary-600 shrink-0" />
                <span className="text-[10px] font-bold text-ink/80 truncate">
                  {t("mockup.note1")}
                </span>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg">
                <FileText size={11} className="text-ink/30 shrink-0" />
                <span className="text-[10px] font-semibold text-ink/45 truncate">
                  {t("mockup.note2")}
                </span>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg">
                <FileText size={11} className="text-ink/30 shrink-0" />
                <span className="text-[10px] font-semibold text-ink/45 truncate">
                  {t("mockup.note3")}
                </span>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg">
                <PenLine size={11} className="text-ink/30 shrink-0" />
                <span className="text-[10px] font-semibold text-ink/45 truncate">
                  {t("mockup.note4")}
                </span>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-dashed border-ink/15 text-ink/35">
              <Plus size={11} />
              <span className="text-[10px] font-semibold">{t("mockup.newNote")}</span>
            </div>
          </div>

          {/* Editor com conteúdo real */}
          <div className="flex-1 p-5">
            <h3 className="font-display text-lg font-extrabold text-ink tracking-tight">
              {t("mockup.editorTitle")}
            </h3>
            <div className="mt-1.5 flex items-center gap-1.5">
              <span className="px-2 py-0.5 rounded-full bg-primary-50 border border-primary-200 text-[9px] font-bold text-primary-700">
                {t("mockup.tagWork")}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-[9px] font-bold text-ink/50">
                {t("mockup.tagPriority")}
              </span>
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-ink/55">
              {t("mockup.editorBody")}
            </p>

            {/* Lista de tarefas: a segunda se completa sozinha em loop */}
            <div className="mt-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded border-2 border-primary-500 bg-primary-500 flex items-center justify-center shrink-0">
                  <Check size={10} strokeWidth={4} className="text-white" />
                </span>
                <span className="text-[11px] font-semibold text-ink/45 line-through decoration-2">
                  {t("mockup.task1")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="hero-check-box w-4 h-4 rounded border-2 flex items-center justify-center shrink-0">
                  <Check
                    size={10}
                    strokeWidth={4}
                    className="hero-check-mark text-white"
                  />
                </span>
                <span className="hero-strike text-[11px] font-semibold text-ink/75">
                  {t("mockup.task2")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 rounded border-2 border-zinc-300 shrink-0" />
                <span className="text-[11px] font-semibold text-ink/75">
                  {t("mockup.task3")}
                </span>
              </div>
            </div>

            {/* Linha "sendo digitada" com cursor piscando */}
            <p className="mt-3 text-[11px] font-medium text-ink/40">
              {t("mockup.typing")}
              <span className="hero-caret ml-0.5 inline-block w-[2px] h-3 align-middle bg-primary-500 rounded-full" />
            </p>
          </div>
        </div>
      </div>

      {/* Flutuante: nota amarela */}
      <div
        className="pointer-events-none absolute -top-8 -right-4 md:-right-8 w-40 p-3.5 rounded-xl bg-amber-100 border-2 border-ink/10 shadow-lg animate-float-slow"
        style={{ "--float-rotate": "4deg" } as React.CSSProperties}
      >
        <p className="text-[11px] font-semibold text-ink/80 leading-snug">
          {t("mockup.floatNote")}
        </p>
      </div>

      {/* Flutuante: tarefa riscada */}
      <div
        className="pointer-events-none absolute -bottom-6 -left-3 md:-left-8 flex items-center gap-2 px-3.5 py-2.5 rounded-full bg-white border-2 border-ink/10 shadow-lg animate-float-slower"
        style={{ "--float-rotate": "-3deg" } as React.CSSProperties}
      >
        <span className="w-4 h-4 rounded-full bg-primary-500 flex items-center justify-center">
          <Check size={10} strokeWidth={4} className="text-white" />
        </span>
        <span className="text-[11px] font-bold text-ink/60 line-through">{t("mockup.floatTask")}</span>
      </div>

      {/* Flutuante: lembrete (só em telas maiores) */}
      <div
        className="pointer-events-none hidden sm:flex absolute top-1/2 -right-6 md:-right-12 items-center gap-2 px-3.5 py-2.5 rounded-full bg-cream border-2 border-primary-300 shadow-lg animate-float-slow"
        style={{ "--float-rotate": "2deg", animationDelay: "1.4s" } as React.CSSProperties}
      >
        <Bell size={13} className="text-primary-600" />
        <span className="text-[11px] font-bold text-ink">{t("mockup.floatReminder")}</span>
      </div>

      {/* Flutuante: mini Quadro (feature nova) com rabisco à mão */}
      <div
        className="pointer-events-none hidden md:block absolute -bottom-10 right-8 w-32 p-2.5 rounded-xl bg-white border-2 border-ink/10 shadow-lg animate-float-slower"
        style={{ "--float-rotate": "3deg", animationDelay: "0.8s" } as React.CSSProperties}
      >
        <div className="flex items-center gap-1 mb-1.5">
          <PenLine size={10} className="text-primary-600" />
          <span className="text-[9px] font-bold text-ink/60">{t("mockup.boardLabel")}</span>
        </div>
        <svg viewBox="0 0 100 44" fill="none" className="w-full">
          <rect x="4" y="6" width="30" height="16" rx="4" stroke="var(--color-primary-500)" strokeWidth="2.5" />
          <path d="M36 14 C 48 14, 50 30, 62 30" stroke="var(--color-ink)" strokeOpacity="0.35" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="76" cy="30" r="9" stroke="var(--color-primary-500)" strokeWidth="2.5" />
          <path d="M10 34 C 20 30, 28 38, 38 34" stroke="var(--color-ink)" strokeOpacity="0.25" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}
